import { useEffect, useState } from 'react';
import { z } from 'zod';
import { api, ApiError } from '@/lib/api';
import { ConfirmDialog } from '@/components/dashboard/ConfirmDialog';
import { useDeskToast } from './desk-feedback';

const blockSchema = z.object({
  id: z.string(),
  vehicleId: z.string(),
  startsOn: z.string(),
  endsOn: z.string(),
  reason: z.string(),
});

type VehicleBlock = z.infer<typeof blockSchema>;

export function VehicleBlocks({ vehicleId }: { vehicleId: string }) {
  const notify = useDeskToast();
  const [blocks, setBlocks] = useState<VehicleBlock[]>([]);
  const [failed, setFailed] = useState(false);
  const [startsOn, setStartsOn] = useState('');
  const [endsOn, setEndsOn] = useState('');
  const [reason, setReason] = useState('');
  const [pendingId, setPendingId] = useState<string | null>(null);
  const [removing, setRemoving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api<unknown>(`/api/vehicles/${vehicleId}/blocks`)
      .then((payload) => {
        if (!cancelled) setBlocks(z.array(blockSchema).parse(payload));
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [vehicleId]);

  async function addBlock() {
    const label = reason.trim();
    if (startsOn === '' || endsOn === '' || label === '') return;
    try {
      const saved = blockSchema.parse(await api<unknown>(`/api/vehicles/${vehicleId}/blocks`, {
        method: 'POST',
        body: JSON.stringify({ startsOn, endsOn, reason: label }),
      }));
      setBlocks((current) => [...current, saved].sort((left, right) => left.startsOn.localeCompare(right.startsOn)));
      setStartsOn('');
      setEndsOn('');
      setReason('');
      notify('Dates blocked.');
    } catch (err) {
      notify(err instanceof ApiError ? err.message : 'Could not block these dates.', 'error');
    }
  }

  async function removeBlock() {
    if (pendingId === null) return;
    setRemoving(true);
    try {
      await api(`/api/vehicles/${vehicleId}/blocks/${pendingId}`, { method: 'DELETE' });
      setBlocks((current) => current.filter((block) => block.id !== pendingId));
      setPendingId(null);
      notify('Blocked dates removed.');
    } catch (err) {
      notify(err instanceof ApiError ? err.message : 'Could not remove the blocked dates.', 'error');
    } finally {
      setRemoving(false);
    }
  }

  return (
    <div className="col-span-2 rounded-2xl border border-line bg-shell/60 p-3">
      <p className="text-xs font-bold text-ink">Blocked dates</p>
      <p className="mt-1 text-[11px] text-muted">The public quote and the desk refuse a rental that touches these days.</p>
      {failed && <p className="mt-2 text-[11px] font-semibold text-danger">Blocked dates could not be loaded.</p>}
      <ul className="mt-2 space-y-1">
        {blocks.map((block) => (
          <li key={block.id} className="flex items-center justify-between gap-2 rounded-xl bg-card px-3 py-2 text-xs">
            <span className="text-ink">
              <span className="font-semibold tabular-nums">{block.startsOn} → {block.endsOn}</span>
              <span className="text-muted"> · {block.reason}</span>
            </span>
            <button type="button" onClick={() => setPendingId(block.id)} className="font-semibold text-danger">Remove</button>
          </li>
        ))}
      </ul>
      <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-3">
        <input type="date" value={startsOn} onChange={(event) => setStartsOn(event.target.value)} aria-label="Block starts" className="rounded-xl border border-line bg-card px-3 py-2 text-xs text-ink" />
        <input type="date" value={endsOn} onChange={(event) => setEndsOn(event.target.value)} aria-label="Block ends" className="rounded-xl border border-line bg-card px-3 py-2 text-xs text-ink" />
        <input value={reason} onChange={(event) => setReason(event.target.value)} aria-label="Block reason" placeholder="Reason" maxLength={160} className="rounded-xl border border-line bg-card px-3 py-2 text-xs text-ink" />
      </div>
      <button type="button" onClick={() => void addBlock()} className="mt-2 rounded-xl bg-ink px-3 py-2 text-xs font-semibold text-shell">Block these dates</button>
      <ConfirmDialog
        open={pendingId !== null}
        title="Remove these blocked dates?"
        description="The car can be quoted and booked again for that range."
        confirmLabel="Remove block"
        pendingLabel="Removing…"
        pending={removing}
        onCancel={() => { if (!removing) setPendingId(null); }}
        onConfirm={() => void removeBlock()}
      />
    </div>
  );
}
